/**
 * Serialization layer for the x-allOf rule builder.
 *
 * Rule (UI state):
 *   { id, negated, ifConditions[], thenFields[], elseFields[], raw? }
 *
 * IfCondition:
 *   { field, operator, value?, required }
 *   `field` is a display path (e.g. 'address.country', 'manifest[].consignee.email').
 *
 * FieldConstraint:
 *   { fieldName, required, type, arrayItemType?, minLength?, maxLength?,
 *     minimum?, maximum?, exclusiveMinimum?, exclusiveMaximum?, pattern?,
 *     format?, enumValues?, containsConst?, nullableInBranch?, raw? }
 *   `fieldName` is a display path too.
 *
 * Schema (persisted JSON, draft 2020-12):
 *   [{ if: {...}, then: {...}, else?: {...} }, ...]
 *
 * The parser is tolerant — unknown keywords land in a `raw` escape hatch so
 * byte-identical round-trips survive even when a human hand-edits the JSON.
 * The emitter merges `raw` last so preserved keywords always win.
 *
 * Phase 1b: paths.js owns path <-> JSON Schema wrapper translation. This file
 * emits/parses leaf operator snippets and delegates nesting to
 * emitPathCondition / parsePathFromProperties.
 */

import {
	displayToPath,
	pathToDisplay,
	emitPathCondition,
	mergePathFragments,
	parsePathFromProperties,
	collectRequiredByPath,
} from './paths';

export const NUMERIC_OPERATORS = new Set([
	'minimum', 'maximum', 'exclusive_minimum', 'exclusive_maximum',
]);

// ============ ID helpers ============

/** djb2 — tiny stable hash for rule content addressing. */
export function djb2Hash(str) {
	let hash = 5381;
	for (let i = 0; i < str.length; i += 1) {
		hash = (hash * 33) ^ str.charCodeAt(i);
	}
	return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Canonical JSON — recursive alpha-sort keys so hashes are order-independent. */
export function canonicalize(value) {
	if (Array.isArray(value)) return value.map(canonicalize);
	if (value && typeof value === 'object') {
		return Object.keys(value).sort().reduce((acc, k) => {
			acc[k] = canonicalize(value[k]);
			return acc;
		}, {});
	}
	return value;
}

export function newRuleId() {
	return `rule-${crypto.randomUUID()}`;
}

// ============ Deep merge (for `raw` escape hatch) ============

function isPlainObject(v) {
	return v !== null && typeof v === 'object' && !Array.isArray(v);
}

export function deepMerge(target, source) {
	if (!isPlainObject(source)) return source === undefined ? target : source;
	const base = isPlainObject(target) ? { ...target } : {};
	for (const [k, v] of Object.entries(source)) {
		base[k] = isPlainObject(v) && isPlainObject(base[k]) ? deepMerge(base[k], v) : v;
	}
	return base;
}

// ============ Value coercion ============

function coerceScalar(raw) {
	if (raw === null || raw === undefined) return raw;
	const s = String(raw).trim();
	if (s === '') return '';
	if (s === 'true') return true;
	if (s === 'false') return false;
	if (s === 'null') return null;
	const n = Number(s);
	return Number.isNaN(n) ? s : n;
}

function coerceList(raw) {
	if (Array.isArray(raw)) return raw.map(coerceScalar);
	return String(raw ?? '')
		.split(',')
		.map((v) => v.trim())
		.filter((v) => v !== '')
		.map(coerceScalar);
}

// ============ Emitter — rules -> x-allOf JSON ============

function emitIfCondition(cond) {
	let snippet;
	switch (cond.operator) {
		case 'const':
			snippet = { const: coerceScalar(cond.value) };
			break;
		case 'enum':
			snippet = { enum: coerceList(cond.value) };
			break;
		case 'not_in':
			snippet = { not: { enum: coerceList(cond.value) } };
			break;
		case 'minimum':
			snippet = { minimum: Number(cond.value) };
			break;
		case 'maximum':
			snippet = { maximum: Number(cond.value) };
			break;
		case 'exclusive_minimum':
			snippet = { exclusiveMinimum: Number(cond.value) };
			break;
		case 'exclusive_maximum':
			snippet = { exclusiveMaximum: Number(cond.value) };
			break;
		case 'contains_const':
			snippet = { contains: { const: coerceScalar(cond.value) } };
			break;
		case 'type_is_null':
			return { type: 'null' };
		default:
			return null;
	}
	// Layer in `type` captured during parse (e.g. source authored
	// `{type: 'integer', const: 18}` in an if leaf). `type_is_null`
	// already authors `type` itself and bypasses this branch above.
	if (cond.type) {
		if (cond.nullableInBranch) {
			return { type: [cond.type, 'null'], ...snippet };
		}
		if (cond.typeWasArray) {
			return { type: [cond.type], ...snippet };
		}
		return { type: cond.type, ...snippet };
	}
	return snippet;
}

// JSON Schema draft-2020-12 doesn't accept `date` / `date-time` as types —
// lower them to `{ type: 'string', format: '<date|date-time>' }` on emit.
function lowerDateLikeType(type) {
	if (type === 'date') return { type: 'string', format: 'date' };
	if (type === 'date-time') return { type: 'string', format: 'date-time' };
	return null;
}

function emitFieldConstraint(field) {
	const out = {};
	// A field parsed from an uploaded schema that had no explicit `type`
	// leaves `field.type == null`. Treat that as "don't emit type" so a
	// `{ const: 0 }` on an integer root doesn't get coerced to `string`.
	const hasExplicitType = field.type != null && field.type !== '';
	const baseType = field.type;
	const lowered = hasExplicitType ? lowerDateLikeType(baseType) : null;

	if (field.type === 'array') {
		out.type = 'array';
		if (field.arrayItemType) {
			const loweredItem = lowerDateLikeType(field.arrayItemType);
			out.items = loweredItem ? loweredItem : { type: field.arrayItemType };
		}
	} else if (lowered) {
		if (field.nullableInBranch) {
			out.type = [lowered.type, 'null'];
		} else if (field.typeWasArray) {
			// Source authored a single-element type array (e.g. `["integer"]`)
			// without null. Round-trip the array form (B5).
			out.type = [lowered.type];
		} else {
			out.type = lowered.type;
		}
		out.format = lowered.format;
	} else if (hasExplicitType && field.nullableInBranch) {
		out.type = [baseType, 'null'];
	} else if (hasExplicitType && field.typeWasArray) {
		out.type = [baseType];
	} else if (hasExplicitType) {
		out.type = baseType;
	}

	if (field.minLength != null && field.minLength !== '') {
		out.minLength = Number.parseInt(field.minLength, 10);
	}
	if (field.maxLength != null && field.maxLength !== '') {
		out.maxLength = Number.parseInt(field.maxLength, 10);
	}
	if (field.minimum != null && field.minimum !== '') {
		out.minimum = parseFloat(field.minimum);
	}
	if (field.maximum != null && field.maximum !== '') {
		out.maximum = parseFloat(field.maximum);
	}
	if (field.exclusiveMinimum != null && field.exclusiveMinimum !== '') {
		out.exclusiveMinimum = parseFloat(field.exclusiveMinimum);
	}
	if (field.exclusiveMaximum != null && field.exclusiveMaximum !== '') {
		out.exclusiveMaximum = parseFloat(field.exclusiveMaximum);
	}
	if (field.pattern) out.pattern = field.pattern;
	if (field.format) out.format = field.format;
	if (Array.isArray(field.enumValues) && field.enumValues.length > 0) {
		out.enum = field.enumValues;
	} else if (typeof field.enumValues === 'string' && field.enumValues.trim() !== '') {
		out.enum = coerceList(field.enumValues);
	}
	if (field.containsConst != null && field.containsConst !== '') {
		out.contains = { const: coerceScalar(field.containsConst) };
	}

	if (isPlainObject(field.raw)) return deepMerge(out, field.raw);
	return out;
}

// If `containerRequired` is an array, convert it to a Set so the emitter
// only marks listed containers as required. If it is `undefined` (pre-fix
// rules, or rules authored fresh in the UI), return `undefined` so the
// emitter falls back to the legacy "always require containers" behaviour.
function containerSetFor(field) {
	if (!Array.isArray(field?.containerRequired)) return undefined;
	return new Set(field.containerRequired);
}

function emitBranch(fields) {
	let acc = {};
	for (const f of fields) {
		// Stubs synthesized from a branch's `required` array (when source
		// authored no matching `properties` entry) contribute via the rule's
		// thenRequiredOrder/elseRequiredOrder. Emitting them here would
		// inject `properties: { name: {} }` the source schema never had,
		// breaking byte-equal round-trip.
		if (f.synthesizedFromRequired) continue;
		const path = displayToPath(f.fieldName);
		if (path.length === 0) continue;
		const leaf = emitFieldConstraint(f);
		const wrapped = emitPathCondition(path, leaf, {
			requireLeaf: f.required !== false,
			requiredContainerDisplayPaths: containerSetFor(f),
		});
		acc = mergePathFragments(acc, wrapped);
	}
	return acc;
}

function emitIfBody(ifConditions) {
	let acc = {};
	for (const c of ifConditions) {
		const snippet = emitIfCondition(c);
		if (snippet === null) continue;
		const path = displayToPath(c.field);
		if (path.length === 0) continue;
		const wrapped = emitPathCondition(path, snippet, {
			requireLeaf: c.required !== false,
			requiredContainerDisplayPaths: containerSetFor(c),
		});
		acc = mergePathFragments(acc, wrapped);
	}
	return acc;
}

/**
 * Emit a single rule. Returns `null` when the rule carries nothing to emit —
 * callers filter these out so the schema never contains a vacuously-true
 * `{ if: { properties: {} } }` entry (which would corrupt every payload).
 * A rule is emittable when EITHER the UI has authored ifConditions that
 * produce a non-empty body, OR the uploaded rule's `raw.if` preserves an
 * unrecognized operator (e.g. `pattern` in IF) via the escape hatch.
 */
export function emitRule(rule) {
	if (!rule) return null;

	const hasConditions = Array.isArray(rule.ifConditions) && rule.ifConditions.length > 0;
	const hasRawIf = isPlainObject(rule.raw?.if);
	if (!hasConditions && !hasRawIf) return null;

	const body = hasConditions ? emitIfBody(rule.ifConditions) : {};
	const bodyEmpty = !isPlainObject(body.properties) || Object.keys(body.properties).length === 0;
	if (hasConditions && bodyEmpty && !hasRawIf) return null;

	const thenBuilt = emitBranch(rule.thenFields || []);
	if (Array.isArray(rule.thenRequiredOrder) && rule.thenRequiredOrder.length > 0) {
		// Preserve source-authored `then.required` even when the branch has
		// no `properties` (so parseBranch returned an empty fields array).
		// The source-authored order is authoritative; any required entries
		// the builder produced from properties walking are unioned in
		// after, in their original sequence.
		thenBuilt.required = unionPreservingOrder(rule.thenRequiredOrder, thenBuilt.required);
	}
	const out = {
		if: rule.negated ? { not: body } : body,
		then: thenBuilt,
	};
	const hasElseFields = Array.isArray(rule.elseFields) && rule.elseFields.length > 0;
	const hasElseRequired = Array.isArray(rule.elseRequiredOrder) && rule.elseRequiredOrder.length > 0;
	if (hasElseFields || hasElseRequired) {
		const elseBuilt = hasElseFields ? emitBranch(rule.elseFields) : {};
		if (hasElseRequired) {
			// Same union as `then` above. Without this, source's
			// `else: { required: [...] }` blocks are dropped on round-trip
			// because parseBranch returns no fields when properties is absent.
			elseBuilt.required = unionPreservingOrder(rule.elseRequiredOrder, elseBuilt.required);
		}
		out.else = elseBuilt;
	}

	if (isPlainObject(rule.raw)) {
		if (rule.raw.if) out.if = deepMerge(out.if, rule.raw.if);
		if (rule.raw.then) out.then = deepMerge(out.then, rule.raw.then);
		if (rule.raw.else) out.else = deepMerge(out.else ?? {}, rule.raw.else);
	}

	// Final guard: after deep-merge with raw.if, the IF body must have at
	// least one property or negated.not.properties entry. A totally empty
	// IF would match every payload.
	const ifCheck = rule.negated ? out.if?.not : out.if;
	const finalIfEmpty = !isPlainObject(ifCheck?.properties)
		|| Object.keys(ifCheck.properties).length === 0;
	if (finalIfEmpty) return null;

	return out;
}

export function rulesToXAllOf(rules) {
	if (!Array.isArray(rules) || rules.length === 0) return null;
	const emitted = rules.map(emitRule).filter((r) => r !== null);
	return emitted.length > 0 ? emitted : null;
}

// ============ Parser — x-allOf JSON -> rules ============

const KNOWN_OP_KEYWORDS = new Set([
	'const', 'enum', 'not',
	'minimum', 'maximum', 'exclusiveMinimum', 'exclusiveMaximum',
	'contains', 'type',
]);

function detectOperator(schema) {
	if (!isPlainObject(schema)) return null;
	if ('const' in schema) {
		return { operator: 'const', value: schema.const };
	}
	if (Array.isArray(schema.enum)) {
		return { operator: 'enum', value: schema.enum };
	}
	if (schema.not && Array.isArray(schema.not.enum)
		&& Object.keys(schema.not).length === 1) {
		return { operator: 'not_in', value: schema.not.enum };
	}
	if ('exclusiveMinimum' in schema) {
		return { operator: 'exclusive_minimum', value: schema.exclusiveMinimum };
	}
	if ('exclusiveMaximum' in schema) {
		return { operator: 'exclusive_maximum', value: schema.exclusiveMaximum };
	}
	if ('minimum' in schema) {
		return { operator: 'minimum', value: schema.minimum };
	}
	if ('maximum' in schema) {
		return { operator: 'maximum', value: schema.maximum };
	}
	if (schema.contains && isPlainObject(schema.contains) && 'const' in schema.contains) {
		return { operator: 'contains_const', value: schema.contains.const };
	}
	if (schema.type === 'null'
		|| (Array.isArray(schema.type) && schema.type.length === 1 && schema.type[0] === 'null')) {
		return { operator: 'type_is_null' };
	}
	return null;
}

function extractUnknownKeywords(schema) {
	if (!isPlainObject(schema)) return null;
	const unknown = {};
	for (const [k, v] of Object.entries(schema)) {
		if (!KNOWN_OP_KEYWORDS.has(k)) unknown[k] = v;
	}
	return Object.keys(unknown).length > 0 ? unknown : null;
}

// Collect the display paths of intermediate PROPERTY segments along `path`
// that the uploaded schema marked required on their parent. Emitters use
// this to decide whether to emit `required: [name]` for each container on
// round-trip, instead of unconditionally adding one at every level (GAP-7).
function collectRequiredContainersForLeaf(path, requiredByPath) {
	if (!Array.isArray(path) || path.length <= 1) return [];
	const out = [];
	for (let i = 0; i < path.length - 1; i += 1) {
		if (path[i].kind !== 'property') continue;
		const display = pathToDisplay(path.slice(0, i + 1));
		if (requiredByPath.get(display) === true) out.push(display);
	}
	return out;
}

function parseIfBody(body) {
	const conds = [];
	const rawProps = {};
	if (!isPlainObject(body)) return { conds, rawProps };

	const requiredByPath = collectRequiredByPath(body);
	const leaves = parsePathFromProperties(body);

	for (const { path, leaf } of leaves) {
		const display = pathToDisplay(path);
		const detected = detectOperator(leaf);
		const unknown = extractUnknownKeywords(leaf);
		if (detected) {
			const cond = {
				field: display,
				operator: detected.operator,
				value: detected.value,
				required: requiredByPath.get(display) === true,
				containerRequired: collectRequiredContainersForLeaf(path, requiredByPath),
			};
			// Capture `type` sitting alongside the operator (e.g.
			// `{type: 'integer', const: 18}`). `type_is_null` already
			// encodes type via the operator itself.
			if (detected.operator !== 'type_is_null' && isPlainObject(leaf)) {
				const rawType = leaf.type;
				if (Array.isArray(rawType)) {
					const nonNull = rawType.find((t) => t !== 'null');
					if (nonNull) {
						cond.type = nonNull;
						if (rawType.includes('null')) cond.nullableInBranch = true;
						cond.typeWasArray = true;
					}
				} else if (rawType) {
					cond.type = rawType;
				}
			}
			conds.push(cond);
			if (unknown) rawProps[display] = unknown;
		} else {
			rawProps[display] = leaf;
		}
	}
	return { conds, rawProps };
}

function parseFieldConstraint(name, prop, required) {
	if (!isPlainObject(prop)) {
		// Mirror the main path: leave `type` unset when the uploaded schema
		// has no object with an explicit `type` keyword. Injecting 'string'
		// here would corrupt boolean/`$ref` subschemas on round-trip.
		return { fieldName: name, required };
	}
	const rawType = prop.type;
	// Leave `type` unset when the uploaded property has no explicit `type`
	// keyword so the emitter can omit it on round-trip. Previously defaulted
	// to `'string'`, which silently changed a `const: 0` on an integer root
	// into `{ const: 0, type: 'string' }`.
	let type = null;
	let nullableInBranch = false;
	let explicitType = false;
	let typeWasArray = false;
	if (Array.isArray(rawType)) {
		const nonNull = rawType.find((t) => t !== 'null');
		type = nonNull || 'string';
		nullableInBranch = rawType.includes('null');
		explicitType = true;
		typeWasArray = true;
	} else if (rawType) {
		type = rawType;
		explicitType = true;
	}

	const field = {
		fieldName: name,
		required,
	};
	if (explicitType) field.type = type;
	if (nullableInBranch) field.nullableInBranch = true;
	// Round-trip the array form even when only a single type was authored
	// (e.g. `["integer"]`). Without this, the emitter collapses to primitive
	// and breaks byte-equal fidelity (B5).
	if (typeWasArray) field.typeWasArray = true;
	if (type === 'array' && isPlainObject(prop.items) && typeof prop.items.type === 'string') {
		field.arrayItemType = prop.items.type;
	}
	if (prop.minLength != null) field.minLength = prop.minLength;
	if (prop.maxLength != null) field.maxLength = prop.maxLength;
	if (prop.minimum != null) field.minimum = prop.minimum;
	if (prop.maximum != null) field.maximum = prop.maximum;
	if (prop.exclusiveMinimum != null) field.exclusiveMinimum = prop.exclusiveMinimum;
	if (prop.exclusiveMaximum != null) field.exclusiveMaximum = prop.exclusiveMaximum;
	if (prop.pattern) field.pattern = prop.pattern;
	if (prop.format) field.format = prop.format;
	if (Array.isArray(prop.enum)) field.enumValues = prop.enum;
	if (isPlainObject(prop.contains) && 'const' in prop.contains) {
		field.containsConst = prop.contains.const;
	}

	const KNOWN_FIELD_KEYWORDS = new Set([
		'type', 'items', 'minLength', 'maxLength',
		'minimum', 'maximum', 'exclusiveMinimum', 'exclusiveMaximum',
		'pattern', 'format', 'enum', 'contains',
	]);
	const raw = {};
	for (const [k, v] of Object.entries(prop)) {
		if (!KNOWN_FIELD_KEYWORDS.has(k)) raw[k] = v;
	}
	if (Object.keys(raw).length > 0) field.raw = raw;
	return field;
}

function parseBranch(branch) {
	if (!isPlainObject(branch)) return [];
	const leaves = parsePathFromProperties(branch);
	const requiredByPath = collectRequiredByPath(branch);
	const fields = leaves.map(({ path, leaf }) => {
		const display = pathToDisplay(path);
		const field = parseFieldConstraint(display, leaf, requiredByPath.get(display) === true);
		field.containerRequired = collectRequiredContainersForLeaf(path, requiredByPath);
		return field;
	});
	// Synthesize stub entries for top-level `required` names that have no
	// matching `properties` leaf in source. Without these, schemas like
	//   then: { required: ['approver_name'] }
	// hydrate the builder with zero selected fields and the checkbox is
	// never pre-checked on load. The stub flag prevents emitBranch from
	// re-emitting a `properties` entry, so the branch's `required` array
	// round-trips byte-equally via thenRequiredOrder/elseRequiredOrder.
	if (Array.isArray(branch.required)) {
		const seen = new Set(fields.map((f) => f.fieldName));
		for (const name of branch.required) {
			if (typeof name !== 'string' || seen.has(name)) continue;
			fields.push({
				fieldName: name,
				required: true,
				containerRequired: [],
				synthesizedFromRequired: true,
			});
			seen.add(name);
		}
	}
	return fields;
}

// Capture branch-level keywords the editor doesn't author (e.g. `anyOf`,
// `not`, `oneOf` nested inside a then/else) so they survive round-trip
// via deepMerge in emitRule. Without this, schemas like site_progress
// (`then.anyOf`) and concrete_cube_test_laboratory (allOf entry that's
// itself `{anyOf: ...}`) silently lose those keywords on save.
function extractBranchRawExtras(branch) {
	if (!isPlainObject(branch)) return null;
	const extras = {};
	for (const [k, v] of Object.entries(branch)) {
		if (k === 'properties' || k === 'required') continue;
		extras[k] = v;
	}
	return Object.keys(extras).length > 0 ? extras : null;
}

// Re-sort an emitted `required` array to follow source's authoring order.
// The branch emitter assembles required[] in field-iteration order, but the
// source's `required` array can be authored in any order independent of
// `properties` insertion. Without this, schemas where required[] disagrees
// with properties order (e.g. notification_to_commissioner_of_building_control's
// `then.required`) lose byte-equal fidelity on round-trip (B3, branch-level).
function reorderBranchRequired(required, requiredOrder) {
	if (!Array.isArray(requiredOrder) || !Array.isArray(required) || required.length === 0) {
		return required;
	}
	const have = new Set(required);
	const seen = new Set();
	const ordered = [];
	for (const name of requiredOrder) {
		if (have.has(name) && !seen.has(name)) {
			ordered.push(name);
			seen.add(name);
		}
	}
	for (const name of required) {
		if (!seen.has(name)) {
			ordered.push(name);
			seen.add(name);
		}
	}
	return ordered;
}

// Union two ordered name lists, preserving the first list's order and
// appending any extras from the second. Used to preserve source's
// then/else `required` ordering on round-trip when source authored a
// `required: [...]` block without a corresponding `properties`. Without
// this, parseBranch returns no fields and the source's required[] is
// dropped (Bug C).
function unionPreservingOrder(primary, secondary) {
	const seen = new Set();
	const out = [];
	if (Array.isArray(primary)) {
		for (const name of primary) {
			if (typeof name === 'string' && !seen.has(name)) {
				out.push(name);
				seen.add(name);
			}
		}
	}
	if (Array.isArray(secondary)) {
		for (const name of secondary) {
			if (typeof name === 'string' && !seen.has(name)) {
				out.push(name);
				seen.add(name);
			}
		}
	}
	return out;
}

function parseRule(entry, index) {
	if (!isPlainObject(entry)) return null;
	const ifNode = entry.if || {};
	const negated = isPlainObject(ifNode.not);
	const body = negated ? ifNode.not : ifNode;
	const { conds, rawProps } = parseIfBody(body);

	const raw = {};
	const bodyRawExtras = {};
	if (isPlainObject(body)) {
		for (const [k, v] of Object.entries(body)) {
			if (k !== 'properties' && k !== 'required') bodyRawExtras[k] = v;
		}
	}
	if (Object.keys(rawProps).length > 0) {
		bodyRawExtras.properties = rawProps;
		// FIX (Bug 3): preserve body.required[] entries for property names that
		// landed in rawProps (unrecognised operators like minItems, minProperties,
		// uniqueItems, etc. that detectOperator() doesn't handle). Those names
		// never produce an ifCondition, so their required flag would otherwise be
		// silently dropped on round-trip. Re-attach them via raw.if.required so
		// emitRule's deepMerge restores the array in the emitted IF body.
		if (Array.isArray(body?.required)) {
			const rawPropNames = new Set(Object.keys(rawProps));
			const requiredForRaw = body.required.filter(
				(name) => typeof name === 'string' && rawPropNames.has(name),
			);
			if (requiredForRaw.length > 0) {
				bodyRawExtras.required = requiredForRaw;
			}
		}
	}
	if (Object.keys(bodyRawExtras).length > 0) {
		raw.if = negated ? { not: bodyRawExtras } : bodyRawExtras;
	}

	const thenExtras = extractBranchRawExtras(entry.then);
	if (thenExtras) raw.then = thenExtras;
	const elseExtras = extractBranchRawExtras(entry.else);
	if (elseExtras) raw.else = elseExtras;

	// Capture each branch's source-authored top-level `required` ordering so
	// the emitter can preserve it verbatim. Without this, the emitter follows
	// properties-iteration order and diverges from source on schemas where
	// `required[]` is authored in a different order (B3, branch-level).
	const thenRequiredOrder = Array.isArray(entry.then?.required) ? entry.then.required : null;
	const elseRequiredOrder = Array.isArray(entry.else?.required) ? entry.else.required : null;

	// Identity must not change when only `then`/`else` are edited — otherwise a
	// modified rule round-trips through upload as removed+added in diffXAllOfRules.
	// Hash only the `if` + negation flag; equality of rule *content* is still
	// enforced via canonicalize(emitRule(rule)) in the diff itself.
	const identityFor = { if: entry.if ?? null, negated };
	const id = `rule-${index}-${djb2Hash(JSON.stringify(canonicalize(identityFor)))}`;

	return {
		id,
		negated,
		ifConditions: conds,
		thenFields: parseBranch(entry.then),
		elseFields: parseBranch(entry.else),
		...(thenRequiredOrder ? { thenRequiredOrder } : {}),
		...(elseRequiredOrder ? { elseRequiredOrder } : {}),
		...(Object.keys(raw).length > 0 ? { raw } : {}),
	};
}

export function xAllOfToRules(xAllOf) {
	if (!Array.isArray(xAllOf)) return [];
	return xAllOf
		.map((entry, i) => parseRule(entry, `x${i}`))
		.filter((r) => r !== null)
		.map((r) => ({ ...r, sourceKey: 'x-allOf' }));
}

// ============ Plain `allOf` rule reader ============
//
// `allOf` and `x-allOf` are semantically identical for the conditional-rule
// data model — `dsv-schema-utils.xAllOfToAllOf` already lowers x-allOf into
// allOf for the runtime. Existing DataElements in admin_buildex_dev store
// rules under plain `allOf`; the editor must read those rules and round-trip
// them back to `allOf` so users can edit existing schemas without losing
// fidelity.
//
// Only entries whose key set is exactly `{if, then?, else?}` are treated as
// rules; anything else (e.g. `{anyOf: ...}`) is preserved as a "residual"
// pass-through entry that the writer re-emits unchanged.

function isConditionalAllOfEntry(entry) {
	if (!isPlainObject(entry)) return false;
	const keys = Object.keys(entry);
	if (!keys.includes('if')) return false;
	if (!keys.includes('then') && !keys.includes('else')) return false;
	return keys.every((k) => k === 'if' || k === 'then' || k === 'else');
}

export function allOfToRulesAndResidual(allOf) {
	if (!Array.isArray(allOf)) return { rules: [], residualAllOf: [], allOfStructure: [] };
	const rules = [];
	const residualAllOf = [];
	// Track the original `allOf` interleaving (rule entries vs residual
	// entries) so the writer can re-emit the same order. Without this,
	// applyUploadedRootMeta concatenates [...residual, ...rules] and breaks
	// schemas where a residual `{anyOf: ...}` block sits AFTER rule entries
	// in source (B4).
	const allOfStructure = [];
	allOf.forEach((entry, i) => {
		if (isConditionalAllOfEntry(entry)) {
			const r = parseRule(entry, `a${i}`);
			if (r) {
				rules.push({ ...r, sourceKey: 'allOf' });
				allOfStructure.push({ kind: 'rule' });
			} else {
				residualAllOf.push(entry);
				allOfStructure.push({ kind: 'residual', value: entry });
			}
		} else {
			residualAllOf.push(entry);
			allOfStructure.push({ kind: 'residual', value: entry });
		}
	});
	return { rules, residualAllOf, allOfStructure };
}

/**
 * Read all conditional rules from a schema, regardless of whether they are
 * stored under `x-allOf` (UI authoring marker) or plain `allOf` (DB-stored
 * canonical form). Returns the parsed rules — each tagged with the
 * `sourceKey` it came from so the writer can round-trip back to the same
 * keyword — plus the residual `allOf` entries (non-rule shapes) that must
 * pass through verbatim.
 */
export function extractRulesFromSchema(schema) {
	const xRules = xAllOfToRules(schema?.['x-allOf']);
	const { rules: aRules, residualAllOf, allOfStructure } = allOfToRulesAndResidual(schema?.allOf);
	return { rules: [...xRules, ...aRules], residualAllOf, allOfStructure };
}

/**
 * Decide which keyword brand-new rules (rules without a sourceKey stamp)
 * should emit under. Preference order:
 *   1. If any existing rule has sourceKey='allOf' → 'allOf' (match what the
 *      existing schema already uses; avoid mixing keywords).
 *   2. Otherwise → 'x-allOf' (preserve current behavior for greenfield
 *      schemas; matches existing tests/fixtures).
 */
export function inferDefaultRuleSourceKey(rules) {
	if (Array.isArray(rules) && rules.some((r) => r?.sourceKey === 'allOf')) {
		return 'allOf';
	}
	return 'x-allOf';
}

/**
 * Group rules by the keyword they should emit under, applying the default
 * sourceKey rule for unstamped (newly-authored) rules.
 */
export function splitRulesBySourceKey(rules) {
	const list = Array.isArray(rules) ? rules : [];
	const defaultKey = inferDefaultRuleSourceKey(list);
	const allOfRules = [];
	const xAllOfRules = [];
	for (const r of list) {
		const key = r?.sourceKey || defaultKey;
		if (key === 'allOf') allOfRules.push(r);
		else xAllOfRules.push(r);
	}
	return { allOfRules, xAllOfRules };
}

/**
 * Emit rules into the two keyword buckets the schema may carry. Returns
 * arrays (never `null`) so callers can append/merge without null checks.
 *   { xAllOf: [...emitted], allOf: [...emitted] }
 */
export function emitRulesByKeyword(rules) {
	const { allOfRules, xAllOfRules } = splitRulesBySourceKey(rules);
	return {
		xAllOf: xAllOfRules.map(emitRule).filter((r) => r !== null),
		allOf: allOfRules.map(emitRule).filter((r) => r !== null),
	};
}
